import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, GoogleMapsContext } from "../App";
import { useTripsStore } from "../store";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import PlacesAutocomplete from "../components/PlacesAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  GoogleMap,
  DirectionsRenderer,
} from "@react-google-maps/api";

import {
  Map,
  MapPin,
  Navigation,
  Plus,
  Trash2,
  ExternalLink,
  Clock,
  Ticket,
  Globe,
  Star,
  ChevronRight,
  Loader2,
  Route,
  Car,
  Save,
  Share2,
  Copy,
  Check,
  Utensils,
  Trees,
  ShoppingBag,
  Music,
  Camera,
  Landmark,
  Building2,
  Mountain,
  Coffee,
  Theater,
  X,
  RotateCcw,
  Footprints,
  Bus,
  TrainFront,
  Bike,
  Locate,
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Category icon mapping
const getCategoryIcon = (category) => {
  const categoryLower = (category || "").toLowerCase();
  
  // Viewpoint
  if (categoryLower.includes("viewpoint") || categoryLower.includes("panoram") || categoryLower.includes("overlook") || categoryLower.includes("scenic")) {
    return { icon: Mountain, color: "from-sky-500 to-blue-600", badge: "bg-sky-500/20 text-sky-400" };
  }
  // Nature
  if (categoryLower.includes("nature") || categoryLower.includes("lake") || categoryLower.includes("waterfall") || categoryLower.includes("forest") || categoryLower.includes("beach") || categoryLower.includes("valley") || categoryLower.includes("national park")) {
    return { icon: Trees, color: "from-green-500 to-emerald-600", badge: "bg-green-500/20 text-green-400" };
  }
  // City
  if (categoryLower === "city" || categoryLower.includes("town") || categoryLower.includes("village")) {
    return { icon: Building2, color: "from-violet-500 to-purple-600", badge: "bg-violet-500/20 text-violet-400" };
  }
  // Cultural/Historic
  if (categoryLower.includes("cultural") || categoryLower.includes("historic") || categoryLower.includes("unesco") || categoryLower.includes("monastery") || categoryLower.includes("church") || categoryLower.includes("cathedral")) {
    return { icon: Landmark, color: "from-amber-500 to-orange-600", badge: "bg-amber-500/20 text-amber-400" };
  }
  // Restaurant/Food
  if (categoryLower.includes("restaurant") || categoryLower.includes("food") || categoryLower.includes("café") || categoryLower.includes("cafe") || categoryLower.includes("dining")) {
    return { icon: Utensils, color: "from-orange-500 to-red-600", badge: "bg-orange-500/20 text-orange-400" };
  }
  // Park/Garden
  if (categoryLower.includes("park") || categoryLower.includes("garden")) {
    return { icon: Trees, color: "from-green-500 to-emerald-600", badge: "bg-green-500/20 text-green-400" };
  }
  // Shopping/Market
  if (categoryLower.includes("shopping") || categoryLower.includes("market")) {
    return { icon: ShoppingBag, color: "from-pink-500 to-rose-600", badge: "bg-pink-500/20 text-pink-400" };
  }
  // Entertainment
  if (categoryLower.includes("entertainment") || categoryLower.includes("theater") || categoryLower.includes("theatre")) {
    return { icon: Theater, color: "from-purple-500 to-violet-600", badge: "bg-purple-500/20 text-purple-400" };
  }
  // Museum
  if (categoryLower.includes("museum") || categoryLower.includes("gallery") || categoryLower.includes("art")) {
    return { icon: Building2, color: "from-blue-500 to-indigo-600", badge: "bg-blue-500/20 text-blue-400" };
  }
  // Landmark
  if (categoryLower.includes("landmark") || categoryLower.includes("monument") || categoryLower.includes("temple") || categoryLower.includes("palace") || categoryLower.includes("castle")) {
    return { icon: Landmark, color: "from-amber-500 to-orange-600", badge: "bg-amber-500/20 text-amber-400" };
  }
  // Mountain/Hiking
  if (categoryLower.includes("mountain") || categoryLower.includes("hiking") || categoryLower.includes("trail") || categoryLower.includes("pass")) {
    return { icon: Mountain, color: "from-teal-500 to-cyan-600", badge: "bg-teal-500/20 text-teal-400" };
  }
  // Experience/Activity
  if (categoryLower.includes("experience") || categoryLower.includes("activity")) {
    return { icon: Camera, color: "from-cyan-500 to-sky-600", badge: "bg-cyan-500/20 text-cyan-400" };
  }
  // Cafe/Coffee
  if (categoryLower.includes("cafe") || categoryLower.includes("coffee")) {
    return { icon: Coffee, color: "from-amber-600 to-yellow-600", badge: "bg-amber-500/20 text-amber-400" };
  }
  // Default
  return { icon: MapPin, color: "from-amber-500 to-orange-600", badge: "bg-amber-500/20 text-amber-400" };
};

// Map container styles - responsive for mobile
const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

// Dark mode map styles
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#64779e" }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "landscape.man_made", elementType: "geometry.stroke", stylers: [{ color: "#334e87" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#023e58" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#283d6a" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6f9ba5" }] },
  { featureType: "poi", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "poi.park", elementType: "geometry.fill", stylers: [{ color: "#023e58" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#3C7680" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "road", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2c6675" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#255763" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#b0d5ce" }] },
  { featureType: "road.highway", elementType: "labels.text.stroke", stylers: [{ color: "#023e58" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "transit", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "transit.line", elementType: "geometry.fill", stylers: [{ color: "#283d6a" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#3a4762" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4e6d70" }] },
];

const defaultCenter = { lat: 48.8566, lng: 2.3522 }; // Paris default

const RouteSights = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const mapRef = useRef(null);
  
  // Use Zustand store for trip planning state
  const {
    destinations,
    sights,
    isLoading: storeLoading,
    routeGenerated,
    setDestinations,
    setSights,
    setLoading: setStoreLoading,
    setRouteGenerated,
    addDestination: storeAddDestination,
    removeDestination: storeRemoveDestination,
    updateDestination: storeUpdateDestination,
    resetTrip,
  } = useTripsStore();
  
  const [loading, setLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [tripName, setTripName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [directions, setDirections] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [travelMode, setTravelMode] = useState("DRIVING");
  const [exploreMode, setExploreMode] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  // Use global Google Maps loader
  const isLoaded = useContext(GoogleMapsContext);
  const loadError = false;

  // Store map instance
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Calculate directions for the route
  const calculateRoute = useCallback(async (dests, mode) => {
    if (!isLoaded || !window.google) return;

    const validDestinations = dests.filter(d => d.city.trim());
    if (validDestinations.length < 2) {
      setDirections(null);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    const origin = validDestinations[0].city + (validDestinations[0].country ? `, ${validDestinations[0].country}` : "");
    const destination = validDestinations[validDestinations.length - 1].city + (validDestinations[validDestinations.length - 1].country ? `, ${validDestinations[validDestinations.length - 1].country}` : "");
    
    const waypoints = validDestinations.slice(1, -1).map(d => ({
      location: d.city + (d.country ? `, ${d.country}` : ""),
      stopover: true,
    }));

    const gmMode = mode || travelMode;
    const travelModeMap = {
      DRIVING: window.google.maps.TravelMode.DRIVING,
      WALKING: window.google.maps.TravelMode.WALKING,
      TRANSIT: window.google.maps.TravelMode.TRANSIT,
      BICYCLING: window.google.maps.TravelMode.BICYCLING,
    };

    try {
      const result = await directionsService.route({
        origin,
        destination,
        waypoints,
        travelMode: travelModeMap[gmMode] || window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      });
      
      setDirections(result);
      
      // Center map on the route
      if (result.routes[0]?.bounds) {
        const bounds = result.routes[0].bounds;
        const center = {
          lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
          lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
        };
        setMapCenter(center);
      }
    } catch (error) {
      toast.error("Could not calculate route. Please check your destinations.");
    }
  }, [isLoaded, travelMode]);

  // Add new destination
  const addDestination = () => {
    const newId = Math.max(...destinations.map(d => d.id)) + 1;
    setDestinations([...destinations, { id: newId, city: "", country: "" }]);
  };

  // Remove destination
  const removeDestination = (id) => {
    if (destinations.length <= 2) {
      toast.error("Minimum 2 destinations required");
      return;
    }
    const newDestinations = destinations.filter(d => d.id !== id);
    setDestinations(newDestinations);
    
    // If route was generated and we still have valid destinations, regenerate
    const validDestinations = newDestinations.filter(d => d.city.trim());
    if (routeGenerated && validDestinations.length >= 2) {
      setTimeout(() => {
        generateRouteWithDestinations(newDestinations);
      }, 100);
    }
  };

  // Update destination - use Zustand store's updateDestination method
  const updateDestination = useCallback((id, field, value) => {
    // Use the store's updateDestination method which handles state correctly
    storeUpdateDestination(id, field, value);
  }, [storeUpdateDestination]);

  // Generate route with specific destinations
  const generateRouteWithDestinations = async (dests) => {
    const validDestinations = dests.filter(d => d.city.trim());
    if (validDestinations.length < 2) {
      setRouteGenerated(false);
      setSights([]);
      setDirections(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/roadtrip/sights`, {
        destinations: validDestinations.map(d => ({
          city: d.city,
          country: d.country || ""
        }))
      });
      setSights(response.data.sights || []);
      setRouteGenerated(true);
      
      // Calculate route on the interactive map
      await calculateRoute(dests, travelMode);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Explore nearby using GPS
  const exploreNearby = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
          let city = "", country = "";
          if (result.results[0]) {
            for (const comp of result.results[0].address_components) {
              if (comp.types.includes("locality")) city = comp.long_name;
              if (comp.types.includes("country")) country = comp.long_name;
            }
          }
          if (!city) city = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          // Set same city as start and end
          setDestinations([
            { id: 1, city, country },
            { id: 2, city, country },
          ]);
          setExploreMode(true);
          setTravelMode("WALKING");
          setMapCenter({ lat: latitude, lng: longitude });
          toast.success(`Located: ${city}. Generating sights in 2km radius...`);
          // Generate sights for single city
          setLoading(true);
          const response = await axios.post(`${API}/roadtrip/sights`, {
            destinations: [{ city, country }, { city, country }],
            explore_radius_km: 2,
          });
          setSights(response.data.sights || []);
          setRouteGenerated(true);
        } catch (err) {
          toast.error("Failed to get location details");
        } finally {
          setGpsLoading(false);
          setLoading(false);
        }
      },
      () => { setGpsLoading(false); toast.error("Location access denied"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Generate route and fetch sights
  const generateRoute = async () => {
    const validDestinations = destinations.filter(d => d.city.trim());
    if (validDestinations.length < 2) {
      toast.error("Please enter at least 2 destinations");
      return;
    }

    setExploreMode(false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/roadtrip/sights`, {
        destinations: validDestinations.map(d => ({
          city: d.city,
          country: d.country || ""
        }))
      });
      setSights(response.data.sights || []);
      setRouteGenerated(true);
      
      // Calculate route on the interactive map
      await calculateRoute(destinations, travelMode);
      
      toast.success("Route generated with sightseeing recommendations!");
    } catch (error) {
      toast.error("Failed to generate route");
    } finally {
      setLoading(false);
    }
  };

  // Save roadtrip
  const saveRoadtrip = async () => {
    if (!tripName.trim()) {
      toast.error("Please enter a trip name");
      return;
    }

    const validDestinations = destinations.filter(d => d.city.trim());
    if (validDestinations.length < 2) {
      toast.error("Need at least 2 destinations to save");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/roadtrip/save`,
        {
          name: tripName,
          destinations: validDestinations.map(d => ({
            city: d.city,
            country: d.country || ""
          })),
          sights: sights
        },
        { withCredentials: true }
      );
      toast.success("Roadtrip saved to your trips!");
      setShowSaveDialog(false);
      setTripName("");
      navigate("/trips");
    } catch (error) {
      toast.error("Failed to save roadtrip");
    } finally {
      setSaving(false);
    }
  };

  // Share roadtrip
  const shareRoadtrip = async () => {
    const validDestinations = destinations.filter(d => d.city.trim());
    if (validDestinations.length < 2) {
      toast.error("Need at least 2 destinations to share");
      return;
    }

    setSharing(true);
    try {
      const response = await axios.post(
        `${API}/roadtrip/share`,
        {
          destinations: validDestinations.map(d => ({
            city: d.city,
            country: d.country || ""
          })),
          sights: sights
        },
        { withCredentials: true }
      );
      setShareUrl(response.data.share_url);
      setShowShareDialog(true);
      toast.success("Share link created!");
    } catch (error) {
      toast.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  // Copy share URL
  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Clear the current route
  const clearRoute = () => {
    setDirections(null);
    setSights([]);
    setRouteGenerated(false);
    setDestinations([
      { id: 1, city: "", country: "" },
      { id: 2, city: "", country: "" },
    ]);
    toast.success("Route cleared");
  };

  // Build Google Maps directions URL
  const getGoogleMapsUrl = () => {
    const validDestinations = destinations.filter(d => d.city.trim());
    if (validDestinations.length < 2) return "#";
    
    const origin = encodeURIComponent(validDestinations[0].city + (validDestinations[0].country ? `, ${validDestinations[0].country}` : ""));
    const destination = encodeURIComponent(validDestinations[validDestinations.length - 1].city + (validDestinations[validDestinations.length - 1].country ? `, ${validDestinations[validDestinations.length - 1].country}` : ""));
    const waypoints = validDestinations.slice(1, -1).map(d => 
      encodeURIComponent(d.city + (d.country ? `, ${d.country}` : ""))
    ).join("|");
    
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    return url;
  };

  // Get valid destinations for display
  const validDestinations = destinations.filter(d => d.city.trim());

  if (loadError) {
    return (
      <div className="page-container flex items-center justify-center">
        <p className="text-red-400">Error loading Google Maps</p>
      </div>
    );
  }

  return (
    <div className="page-container pb-28" data-testid="route-sights-page">
      {/* Hero Banner */}
      <div className="relative h-32 sm:h-40 overflow-hidden mb-4">
        <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5">
          <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">Route & Sights</h1>
          <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Plan your roadtrip</p>
        </div>
      </div>

      <div className="p-4 pt-0">

        {/* Desktop: Side-by-side layout for planner and map */}
        <div className="lg:flex lg:gap-6 lg:items-start">
          {/* Roadtrip Planner - Compact on desktop */}
          <div className="glass-card p-4 mb-6 lg:mb-0 lg:w-[400px] lg:flex-shrink-0 overflow-hidden">
            <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Car className="w-5 h-5 text-emerald-400" />
              Plan Your Roadtrip
            </h2>

            {/* Travel Mode Selector */}
            <div className="flex gap-1 mb-3 p-1 bg-slate-800/60 rounded-lg" data-testid="travel-mode-selector">
              {[
                { mode: "WALKING", icon: Footprints, label: "Walk" },
                { mode: "TRANSIT", icon: Bus, label: "Bus" },
                { mode: "TRANSIT", icon: TrainFront, label: "Metro", key: "metro" },
                { mode: "BICYCLING", icon: Bike, label: "Bike" },
                { mode: "DRIVING", icon: Car, label: "Car" },
              ].map((t) => {
                const key = t.key || t.mode;
                const isActive = travelMode === t.mode && (!t.key || t.key === "metro");
                return (
                  <button key={key} onClick={() => setTravelMode(t.mode)}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[10px] font-medium transition-all ${travelMode === t.mode ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "text-slate-400 hover:text-slate-300"}`}
                    data-testid={`mode-${t.label.toLowerCase()}`}
                  >
                    <t.icon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Explore Nearby Button */}
            <button
              onClick={exploreNearby}
              disabled={gpsLoading}
              className={`w-full mb-3 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-all ${exploreMode ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" : "bg-white/[0.03] text-slate-300 border-white/10 hover:border-cyan-500/30 hover:text-cyan-300"}`}
              data-testid="explore-nearby-btn"
            >
              {gpsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
              {gpsLoading ? "Locating..." : "Explore Nearby (2km radius)"}
            </button>
            
            <div className="space-y-1">
              {destinations.map((dest, index) => (
                <div key={dest.id} className="flex items-start gap-2">
                  {/* Step indicator */}
                  <div className="flex flex-col items-center pt-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      index === 0 
                        ? "bg-emerald-500 text-white" 
                        : index === destinations.length - 1 
                          ? "bg-rose-500 text-white"
                          : "bg-slate-700 text-slate-300"
                    }`}>
                      {index + 1}
                    </div>
                    {index < destinations.length - 1 && (
                      <div className="w-0.5 h-2 bg-slate-600 my-0.5" />
                    )}
                  </div>
                  
                  {/* Input fields */}
                  <div className="flex-1 min-w-0 grid grid-cols-[1fr_72px] sm:grid-cols-[1fr_100px] lg:grid-cols-[1fr_96px] gap-1.5">
                    <PlacesAutocomplete
                      type="city"
                      placeholder={index === 0 ? "Start city..." : index === destinations.length - 1 ? "End city..." : "Stop city..."}
                      value={dest.city}
                      onChange={(e) => updateDestination(dest.id, "city", e.target.value)}
                      onPlaceSelect={({ city, country }) => {
                        if (city) updateDestination(dest.id, "city", city);
                        if (country) updateDestination(dest.id, "country", country);
                      }}
                      className="w-full bg-slate-800/50 border-slate-700/50 text-white text-sm lg:text-xs"
                      data-testid={`destination-city-${index}`}
                    />
                    <PlacesAutocomplete
                      type="country"
                      placeholder="Country"
                      value={dest.country}
                      onChange={(e) => updateDestination(dest.id, "country", e.target.value)}
                      onPlaceSelect={({ country }) => {
                        if (country) updateDestination(dest.id, "country", country);
                      }}
                      className="w-full min-w-0 bg-slate-800/50 border-slate-700/50 text-white text-sm lg:text-xs"
                      data-testid={`destination-country-${index}`}
                    />
                  </div>
                  
                  {/* Remove button */}
                  {destinations.length > 2 && (
                    <button
                      onClick={() => removeDestination(dest.id)}
                      className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors mt-1"
                      data-testid={`remove-destination-${index}`}
                    >
                      <Trash2 className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={addDestination}
                variant="outline"
                size="sm"
                className="flex-1 btn-outline text-sm"
                data-testid="add-destination-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Stop
              </Button>
              <Button
                onClick={generateRoute}
                disabled={loading}
                size="sm"
                className="flex-1 btn-primary text-sm"
                data-testid="generate-route-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4 mr-1" />
                )}
                Generate
              </Button>
            </div>

            {/* Save & Share Buttons */}
            {routeGenerated && validDestinations.length >= 2 && (
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => setShowSaveDialog(true)}
                  variant="outline"
                  size="sm"
                  className="flex-1 btn-outline text-sm"
                  data-testid="save-roadtrip-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
                <Button
                  onClick={shareRoadtrip}
                  disabled={sharing}
                  variant="outline"
                  size="sm"
                  className="flex-1 btn-outline text-sm"
                  data-testid="share-roadtrip-btn"
                >
                  {sharing ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-1" />
                  )}
                  Share
                </Button>
              </div>
            )}

            {/* Route Summary - visible on desktop in the planner card */}
            {routeGenerated && validDestinations.length >= 2 && (
              <div className="hidden lg:block mt-4 pt-4 border-t border-slate-700/50">
                <div className="flex flex-wrap items-center gap-1">
                  {validDestinations.map((dest, idx) => (
                    <div key={dest.id} className="flex items-center">
                      <div className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                        idx === 0
                          ? "bg-emerald-500/20 text-emerald-400"
                          : idx === validDestinations.length - 1
                          ? "bg-rose-500/20 text-rose-400"
                          : "bg-slate-800/50 text-slate-300"
                      }`}>
                        <MapPin className="w-3 h-3" />
                        <span className="whitespace-nowrap">{dest.city}</span>
                      </div>
                      {idx < validDestinations.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-slate-600 mx-0.5 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Interactive Google Map - Show when route is generated OR always show on desktop */}
          <div className={`glass-card p-4 mb-6 lg:flex-1 ${!routeGenerated ? 'hidden lg:block' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <Map className="w-5 h-5 text-emerald-400" />
                {routeGenerated ? 'Your Route' : 'Map Preview'}
              </h2>
              {routeGenerated && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={clearRoute}
                    className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors"
                    data-testid="clear-route-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                  <a
                    href={getGoogleMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1"
                    data-testid="open-google-maps"
                  >
                    <span className="hidden sm:inline">Open in</span> Google Maps
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
            
            {/* Map container */}
            <div className="h-[350px] sm:h-[400px] lg:h-[500px] rounded-xl overflow-hidden border border-white/10">
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={6}
                  onLoad={onMapLoad}
                  options={{
                    styles: darkMapStyles,
                    disableDefaultUI: false,
                    zoomControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                  }}
                  data-testid="interactive-google-map"
                >
                  {directions && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{
                        draggable: false,
                        suppressMarkers: false,
                        polylineOptions: {
                          strokeColor: "#10b981",
                          strokeWeight: 5,
                          strokeOpacity: 0.8,
                        },
                      }}
                    />
                  )}
                </GoogleMap>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-800/50">
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                </div>
              )}
            </div>

            {/* Route Summary - Mobile only */}
            {routeGenerated && validDestinations.length >= 2 && (
              <div className="lg:hidden flex items-center gap-2 mt-4 overflow-x-auto pb-2">
                {validDestinations.map((dest, idx) => (
                  <div key={dest.id} className="flex items-center">
                    <div className={`px-3 py-2 rounded-lg flex items-center gap-2 flex-shrink-0 ${
                      idx === 0
                        ? "bg-emerald-500/20 text-emerald-400"
                        : idx === validDestinations.length - 1
                        ? "bg-rose-500/20 text-rose-400"
                        : "bg-slate-800/50 text-slate-300"
                    }`}>
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm whitespace-nowrap">{dest.city}</span>
                    </div>
                    {idx < validDestinations.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-slate-600 mx-1 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sightseeing Recommendations */}
        {sights.length > 0 && (
          <div className="glass-card p-4">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              Along Your Route ({sights.length} stops)
            </h2>
            
            <ScrollArea className="h-[400px] lg:h-[500px]">
              {/* Grid: 1 column on mobile, 2 columns on desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pr-2">
                {sights.map((sight, idx) => {
                  const categoryStyle = getCategoryIcon(sight.category);
                  const IconComponent = categoryStyle.icon;
                  
                  return (
                    <div
                      key={idx}
                      className={`p-4 bg-slate-800/50 rounded-xl border transition-all ${
                        sight.highlight 
                          ? "border-amber-500/50 ring-1 ring-amber-500/20" 
                          : "border-slate-700/50 hover:border-slate-600/50"
                      }`}
                      data-testid={`sight-${idx}`}
                    >
                      {/* Image */}
                      {sight.image_url && (
                        <div className="mb-3 rounded-lg overflow-hidden h-32 bg-slate-700/50">
                          <img 
                            src={sight.image_url} 
                            alt={sight.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br ${categoryStyle.color} flex items-center justify-center flex-shrink-0`}>
                          <IconComponent className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div>
                            <h3 className="font-semibold text-white text-sm lg:text-base break-words">{sight.name}</h3>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {sight.highlight && (
                                <span className="px-1.5 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400 whitespace-nowrap">Must-see</span>
                              )}
                              {sight.rating && (
                                <div className="flex items-center gap-1 text-amber-400">
                                  <Star className="w-3.5 h-3.5 fill-current" />
                                  <span className="text-xs">{sight.rating}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <p className="text-xs text-slate-400">{sight.location}</p>
                            {sight.category && (
                              <span className={`px-1.5 py-0.5 rounded-full text-xs ${categoryStyle.badge}`}>
                                {sight.category}
                              </span>
                            )}
                            {sight.distance_info && (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">
                                {sight.distance_info}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-300 mt-2 line-clamp-2">{sight.description}</p>
                          
                          {/* Extra Info: Hours, Price */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {sight.opening_hours && (
                              <span className="flex items-center gap-1 text-xs text-sky-400">
                                <Clock className="w-3 h-3" />
                                {sight.opening_hours}
                              </span>
                            )}
                            {sight.ticket_price && (
                              <span className="flex items-center gap-1 text-xs text-emerald-400">
                                <Ticket className="w-3 h-3" />
                                {sight.ticket_price}
                              </span>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <a
                              href={`https://www.google.com/maps/search/${encodeURIComponent(sight.name + ", " + sight.location)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/30 transition-colors"
                              data-testid={`sight-maps-${idx}`}
                            >
                              <MapPin className="w-3 h-3" />
                              Map
                            </a>
                            {sight.website && (
                              <a
                                href={sight.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-2 py-1 bg-sky-500/20 text-sky-400 rounded-lg text-xs hover:bg-sky-500/30 transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Website
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Empty State */}
        {!routeGenerated && (
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
              <Navigation className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Plan Your Adventure</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Enter your destinations above and click "Generate Route" to see an interactive route on Google Maps 
              and get sightseeing recommendations along the way. You can drag the route to modify it!
            </p>
          </div>
        )}
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Save className="w-5 h-5 text-emerald-400" />
              Save Roadtrip
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Trip Name</label>
              <Input
                placeholder="e.g., European Adventure"
                value={tripName}
                onChange={(e) => setTripName(e.target.value)}
                className="bg-slate-800/50 border-slate-700/50 text-white"
                data-testid="trip-name-input"
              />
            </div>
            <div className="text-sm text-slate-400">
              <p className="mb-2">Route:</p>
              <div className="flex items-center gap-2 flex-wrap">
                {validDestinations.map((dest, idx) => (
                  <span key={dest.id} className="flex items-center gap-1">
                    <span className="text-white">{dest.city}</span>
                    {idx < validDestinations.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 btn-outline"
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={saveRoadtrip}
                disabled={saving || !tripName.trim()}
                className="flex-1 btn-primary"
                data-testid="confirm-save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Trip
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-sky-400" />
              Share Your Route
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-400">
              Share this link with friends so they can see your roadtrip:
            </p>
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 bg-slate-800/50 border-slate-700/50 text-white"
                data-testid="share-url-input"
              />
              <Button
                onClick={copyShareUrl}
                className="btn-primary"
                data-testid="copy-url-btn"
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              This link will expire in 30 days.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 btn-outline"
                onClick={() => setShowShareDialog(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RouteSights;
